<template>
  <v-chip
    class="pl-4 pr-3 !font-bold uppercase"
    :class="`v-chip--size-${size}`"
    size="small"
    v-bind="chipProps"
  />
</template>

<script lang="ts">
import { formatSentence } from '@policyfly/utils/string'
import { computed, defineComponent } from 'vue'

import { isReinstatement } from '@/utils/policy'

import type { ApplicationCategory } from '@policyfly/types/application'
import type { ApplicationKind } from 'types/application'
import type { PropType } from 'vue'

export default defineComponent({
  name: 'PolicyChip',

  props: {
    type: { type: String as PropType<'policy' | 'application'>, required: true },
    kind: { type: String as PropType<ApplicationKind>, required: true },
    category: { type: String as PropType<ApplicationCategory>, required: true },
    size: { type: String as PropType<'xs' | '2xs'>, default: 'xs' },
  },

  setup (props) {
    const reinstatement = computed(() => isReinstatement(props))
    const cancellation = computed(() => props.type === 'application' && props.category === 'CAN')
    const extension = computed(() => props.type === 'application' && props.category === 'NPB')

    const chipProps = computed((): Record<string, string> => {
      const { type, kind } = props
      switch (true) {
        case type === 'policy':
          return {
            text: 'Policy',
            prependIcon: 'mdi-briefcase',
            color: 'info-darken-2',
            class: 'bg-info-lighten-2',
          }
        case cancellation.value:
          return {
            text: 'Cancellation',
            prependIcon: 'mdi-file-document-remove',
            color: 'warning-darken-2',
            class: 'bg-warning-lighten-1',
          }
        default: {
          let text: string = formatSentence(kind)
          let prependIcon = 'mdi-file-document-multiple'
          if (reinstatement.value) {
            text = 'Reinstatement'
            prependIcon = 'mdi-file-document-arrow-right'
          } else if (extension.value) {
            text = 'Extension'
            prependIcon = 'mdi-file-document-plus'
          } else if (kind === 'APPLICATION') {
            text = 'New Application'
            prependIcon = 'mdi-file-document'
          }
          return {
            text,
            prependIcon,
            color: 'success-darken-2',
            class: 'bg-success-lighten-1',
          }
        }
      }
    })

    return {
      chipProps,
    }
  },
})
</script>

<style lang="sass" scoped>
$outside-spacing: 6px
$overlap: 2px
$transition-duration: 0.5s

.coverage-indicator
  display: flex
  margin-left: $overlap
  padding: 0 $outside-spacing
  user-select: none
.coverage-chip
  display: flex
  align-items: center
  justify-content: center
  flex: 0 0 auto
  height: 16px
  width: auto
  padding: 0 4px
  min-width: 16px
  max-width: 16px
  margin-left: -$overlap
  overflow: hidden
  border: 1px solid white
  border-radius: 8px
  transition: max-width $transition-duration
  z-index: 0
  span
    color: white
    font-size: 8px
    font-weight: bold
    line-height: 8px
    white-space: nowrap
  .coverage-full
    opacity: 0
    max-width: 0
    transition: max-width $transition-duration, opacity 0.25s
  &:hover
    max-width: 28px
    z-index: 1
    .coverage-full
      opacity: 1
      max-width: 12px
</style>
